import { cap } from '../../../State'
import { translations } from '../../../translations'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Formatta millisecondi in formato leggibile (es. "1.2s", "3m 45s")
 * @param {number} ms - Tempo in millisecondi
 * @param {boolean} short - Se true, formato abbreviato
 * @returns {string} Tempo formattato
 */
export const formatMs = (ms, short = false) => {
  if (ms == null || isNaN(ms)) return '-'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  if (short) return `${minutes}m${remainingSeconds}s`
  return `${minutes}m ${remainingSeconds}s`
}

/**
 * Formatta una data ISO in formato leggibile
 * @param {string|Date} date - Data da formattare
 * @param {boolean} withTime - Se includere l'orario
 * @returns {string} Data formattata
 */
export const formatDate = (date, withTime = true) => {
  if (!date) return '-'
  const d = new Date(date)
  const day = d.getDate().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const hours = d.getHours().toString().padStart(2, '0')
  const minutes = d.getMinutes().toString().padStart(2, '0')
  if (withTime) return `${day}/${month} ${hours}:${minutes}`
  return `${day}/${month}/${d.getFullYear()}`
}

/**
 * Formatta una chiave giorno YYYY-MM-DD come etichetta gg/mm/aaaa
 * @param {string} dayKey - Data solo giorno (ISO)
 * @returns {string} Etichetta leggibile
 */
export const formatDayKeyLabel = (dayKey) => {
  if (!dayKey || dayKey.length < 10) return dayKey || '-'
  const [y, m, d] = dayKey.split('-')
  return `${d}/${m}/${y}`
}

export const STATE_COLORS = {
  critical: 'var(--danger)',
  warning: 'var(--warning)',
  ok: 'var(--success)'
}

export const STATE_LABELS = {
  critical: t.pfStatoCritico,
  warning: t.pfStatoAttenzione,
  ok: t.pfStatoNormale
}

export const STEP_STATE_LABELS = {
  3: t.pfStepCompletato,
  5: t.errore
}

const CHART_COLORS = [
  '#2563eb',
  '#ef4444',
  '#22c55e',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
  '#0ea5e9',
  '#84cc16'
]

export const getChartColor = (index) => CHART_COLORS[index % CHART_COLORS.length]

/**
 * Genera le date di inizio/fine per i preset temporali
 * @param {string} preset - Chiave del preset
 * @returns {{ startDate: Date, endDate: Date }}
 */
export const getPresetDates = (preset) => {
  const now = new Date()
  const end = new Date(now)
  let start

  switch (preset) {
    case '1h':
      start = new Date(now.getTime() - 60 * 60 * 1000)
      break
    case '6h':
      start = new Date(now.getTime() - 6 * 60 * 60 * 1000)
      break
    case '24h':
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      break
    case '7d':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      break
    case '30d':
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      break
    default:
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000)
  }

  return { startDate: start, endDate: end }
}

export const PRESETS = [
  { key: '1h', label: t.pfPresetUltimaOra },
  { key: '6h', label: t.pfPreset6h },
  { key: '24h', label: t.pfPreset24h },
  { key: '7d', label: t.pfPreset7Giorni },
  { key: '30d', label: t.pfPreset30Giorni }
]

/**
 * Formatta secondi in formato leggibile (es. "1m 23s", "45.2s")
 * @param {number} seconds - Tempo in secondi
 * @param {boolean} short - Se true, formato abbreviato senza millisecondi
 * @returns {string} Tempo formattato
 */
export const formatSeconds = (seconds, short = false) => {
  if (seconds == null || isNaN(seconds)) return '-'
  const minutes = Math.floor(seconds / 60)
  const secs = seconds - minutes * 60
  if (minutes > 0)
    return short ? `${minutes}m${Math.floor(secs)}s` : `${minutes}m ${Math.floor(secs)}s`
  return `${secs.toFixed(1)}s`
}
